
import cx from 'classnames';
import { useFormContext } from 'react-hook-form';

import { getError } from '@/utils';

import Text from '../Text';
import ErrorText from '../ErrorText';
import useBreakpoint from '@/hooks/useBreakpoint';

const CheckboxCheck = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox='0 0 24 17'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <path
      d='M1.5 7.25L9.375 15.125L22.5 2'
      stroke='white'
      strokeWidth='3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

type CheckboxProps = JSX.IntrinsicElements['input'] & {
  name: string;
  id?: string;
  children?: React.ReactNode;
};

const Checkbox = ({ id, name, children, ...rest }: CheckboxProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext() || {};
  const error = getError(errors, name);
  const errorID = `checkbox-error-${name}`;
  const { isOnBreakpoint: isTableOrLess } = useBreakpoint(1024);

  const classNames = cx(
    'form--checkbox__label select-none cursor-pointer py-1 px-2 rounded-lg overflow-hidden transition duration-200 hover:bg-gray-300 flex items-center',
    {
      'form--checkbox__label--error': error,
    },
  );
  const textClassNames = cx('pl-3 text-sm flex-1', {
    'text-tim-primary': !error,
    'text-tim-red': error,
  });
  const iconClassNames = cx(
    'form--checkbox__iconBlock relative rounded-lg border-2 transition duration-200 text-tim-primary min-w-8 min-h-8 w-8 h-8 lg:min-w-5 lg:min-h-5 lg:w-5 lg:h-5',
    {
      'border-gray-300': !error,
      'border-tim-red': error,
    },
  );

  return (
    <div className='form--checkbox'>
      {error && <ErrorText id={errorID}>{error}</ErrorText>}
      <input
        {...register(name)}
        className='form--checkbox__input absolute -z-1 opacity-0'
        name={name}
        type='checkbox'
        id={id || name}
        {...rest}
      />
      <label className={classNames} htmlFor={id || name}>
        <span className={iconClassNames}>
          <CheckboxCheck
            className={cx('form--checkbox__icon stroke-2', {
              'form--checkbox__icon__desktop': !isTableOrLess,
              'form--checkbox__icon__mobile': isTableOrLess,
            })}
          />
        </span>
        <Text as='span' className={textClassNames}>
          {children}
        </Text>
      </label>
    </div>
  );
};

export default Checkbox;
